import type { Position, QuoteHeader, QuoteItem } from './schema';
import { isExcludedFromMainSum } from './quote-totals';

export interface PdfImportPosition {
  positionNumber?: string;
  beschreibung: string;
  /** GAEB-artiger Langtext, getrennt vom Kurztext (beschreibung). Nur gesetzt, wenn die
   * Quelle wirklich zwei Texte liefert — existiert nur ein Text, bleibt das Feld leer. */
  longText?: string;
  menge: number;
  einheit: string;
  einzelpreis: number;
  gesamtpreis: number;
  /** true = "kein Preis erfasst" (Quelle nennt keinen Preis). einzelpreis ist dann
   * numerisch 0, aber die 0 ist KEINE Aussage des Dokuments — anders als bei echten
   * 0,00-€-Positionen (681 Stück im realen Kundendokument), die preis 0 OHNE Flag tragen. */
  preisFehlt?: boolean;
  gewerk?: string;
  isBedarfsposition?: boolean;
  isAlternativposition?: boolean;
}

export interface PdfImportMetadata {
  documentNumber?: string;
  date?: string;
  customerName?: string;
  customerAddress?: string;
  projectName?: string;
  totalNet?: number;
  totalGross?: number;
  vatRate?: number;
  vatAmount?: number;
  introText?: string;
  closingText?: string;
}

/** Über welchen Weg das Ergebnis entstanden ist:
 * - 'deterministic': strukturbasierter LV-Parser, jede Zahl aus dem Dokument gelesen
 * - 'text' / 'vision': KI-Auswertung (Textanalyse bzw. PDF-OCR) */
export type PdfImportMethod = 'deterministic' | 'text' | 'vision';

/** Ergebnis der EINEN Abnahme, die hinter beiden Importwegen läuft: Summe der
 * importierten Hauptpositionen gegen die dokumenteigene Schlusssumme, Abweichung in Cent.
 * `verified` ist nur dann true, wenn ein dokumenteigener Vergleichswert existiert UND
 * die Abweichung innerhalb der Toleranz liegt — "keine Summe gefunden" ist NICHT grün. */
export interface PdfImportVerification {
  positionCount: number;
  /** Netto-Summe der importierten Hauptpositionen (ohne Bedarfs-/Alternativpositionen), EUR. */
  positionsTotalNet: number;
  /** Wert, der tatsächlich verglichen wurde (bei Brutto-Basis der aufgerechnete Netto-Wert), EUR. */
  comparableTotal?: number;
  /** Dokumenteigene Schlusssumme, gegen die verglichen wurde, EUR. */
  documentedTotal?: number;
  comparisonBasis?: 'net' | 'gross';
  /** comparableTotal − documentedTotal, in Cent. */
  deviationCents?: number;
  /** Toleranz in Cent, mit der geprüft wurde (0 = centgenau). */
  toleranceCents?: number;
  verified: boolean;
  failureReason?: 'no_documented_total' | 'deviation_exceeds_tolerance' | 'no_positions';
}

/** Toleranz der KI-Abnahme (deterministischer Weg prüft mit toleranceCents = 0). */
export const PDF_IMPORT_SUM_TOLERANCE_ABS_CENTS = 100;
export const PDF_IMPORT_SUM_TOLERANCE_REL = 0.01;

/**
 * Die gemeinsame Verifikation hinter BEIDEN Importwegen (deterministisch und KI).
 * Vergleicht die Summe der Hauptpositionen mit der dokumenteigenen Schlusssumme —
 * bevorzugt Netto; nur wenn ausschließlich Brutto dokumentiert ist, wird die
 * Positionssumme mit dem erkannten MwSt-Satz aufgerechnet und gegen Brutto geprüft.
 */
export function verifyPdfImportPositions(
  positions: ReadonlyArray<PdfImportPosition>,
  documentedTotals: PdfImportResult['documentedTotals'],
  vatRate: number | undefined,
  options?: { toleranceCents?: number },
): PdfImportVerification {
  const positionsTotalNet = Math.round(positions
    .filter((position) => !isExcludedFromMainSum(position))
    .reduce((sum, position) => sum + position.gesamtpreis, 0) * 100) / 100;
  const base: PdfImportVerification = {
    positionCount: positions.length,
    positionsTotalNet,
    verified: false,
  };

  const documentedNet = documentedTotals?.totalNet;
  const documentedGross = documentedTotals?.totalGross;
  let comparableTotal: number | undefined;
  let documentedTotal: number | undefined;
  let comparisonBasis: 'net' | 'gross' | undefined;

  if (typeof documentedNet === 'number' && Number.isFinite(documentedNet) && documentedNet >= 0) {
    comparableTotal = positionsTotalNet;
    documentedTotal = documentedNet;
    comparisonBasis = 'net';
  } else if (
    typeof documentedGross === 'number'
    && Number.isFinite(documentedGross)
    && documentedGross >= 0
    && typeof vatRate === 'number'
    && Number.isFinite(vatRate)
    && vatRate >= 0
  ) {
    comparableTotal = Math.round(positionsTotalNet * (1 + (vatRate / 100)) * 100) / 100;
    documentedTotal = documentedGross;
    comparisonBasis = 'gross';
  }

  if (comparableTotal === undefined || documentedTotal === undefined) {
    return { ...base, failureReason: 'no_documented_total' };
  }

  const deviationCents = Math.round(comparableTotal * 100) - Math.round(documentedTotal * 100);
  const toleranceCents = options?.toleranceCents ?? Math.max(
    PDF_IMPORT_SUM_TOLERANCE_ABS_CENTS,
    Math.round(documentedTotal * 100 * PDF_IMPORT_SUM_TOLERANCE_REL),
  );
  const withinTolerance = Math.abs(deviationCents) <= toleranceCents;
  const hasPositions = positions.length > 0;
  return {
    ...base,
    comparableTotal,
    documentedTotal,
    comparisonBasis,
    deviationCents,
    toleranceCents,
    verified: withinTolerance && hasPositions,
    failureReason: !withinTolerance
      ? 'deviation_exceeds_tolerance'
      : (hasPositions ? undefined : 'no_positions'),
  };
}

export interface PdfImportResult {
  success: boolean;
  documentType: 'angebot' | 'rechnung' | 'unknown';
  metadata: PdfImportMetadata;
  positions: PdfImportPosition[];
  rawText?: string;
  error?: string;
  method?: PdfImportMethod;
  warnings?: string[];
  truncated?: boolean;
  /** true, wenn der extrahierte Dokumenttext vor der Analyse auf MAX_TEXT_INPUT_CHARS
   * gekürzt wurde — der Rest des Dokuments (vermutlich weitere Positionen) wurde dann
   * NIE an das Modell geschickt. Unabhängig von `truncated` (das die Modell-ANTWORT betrifft). */
  inputTruncated?: boolean;
  /** Totals explicitly read from the source document, before any position-based derivation. */
  documentedTotals?: Pick<PdfImportMetadata, 'totalNet' | 'totalGross'>;
  /** Ergebnis der gemeinsamen Abnahme (siehe verifyPdfImportPositions) — für den Nutzer
   * sichtbar: welcher Weg lief und wie zuverlässig das Ergebnis ist. */
  verification?: PdfImportVerification;
}

export interface PdfImportApiData extends Omit<PdfImportResult, 'success' | 'rawText' | 'error' | 'documentedTotals'> {
  positionCount: number;
}

export interface PdfImportOfferDraft {
  quoteItems: QuoteItem[];
  positions: Position[];
}

export interface PdfImportOfferStateDraft extends PdfImportOfferDraft {
  quoteHeader: QuoteHeader;
}

export function normalizePdfImportVatRate(vatRate: unknown): 0 | 7 | 19 {
  return vatRate === 0 || vatRate === 7 || vatRate === 19 ? vatRate : 19;
}

/**
 * `includePrices` steuert, ob Einzelpreise aus dem Quelldokument mit übernommen werden.
 * Default `true` = bisheriges Verhalten unverändert.
 *
 * Preis-Semantik: `preis` bleibt Pflichtfeld `number` (0 bei abgewähltem Kästchen),
 * aber zusätzlich wird `preisFehlt: true` gesetzt (shared/schema.ts). Damit ist
 * "kein Preis erfasst" von echten 0,00-€-Positionen (Bedarfs-/Alternativpositionen
 * mit realem Nullpreis) unterscheidbar — Ausgabewege (PDF, DOCX, Editor) drucken
 * für solche Zeilen "–" statt 0,00 € (shared/quote-totals.ts, hasMissingPrice).
 */
export function mapPdfImportPositionsToOfferRows(
  imported: ReadonlyArray<PdfImportPosition>,
  vatRate: number,
  options?: { includePrices?: boolean },
): PdfImportOfferDraft {
  const includePrices = options?.includePrices ?? true;
  const normalizedVat = normalizePdfImportVatRate(vatRate);
  const quoteItems: QuoteItem[] = imported.map((item, index) => ({
    id: index + 1,
    originalId: item.positionNumber || `${index + 1}`,
    positionNumber: item.positionNumber,
    name: item.beschreibung.substring(0, 50),
    beschreibung: item.beschreibung,
    // Langtext getrennt vom Kurztext mitführen (Kurz-/Langtext-Umschalter);
    // Quellen mit nur einem Text lassen das Feld leer.
    longText: item.longText || undefined,
    gewerk: item.gewerk || 'Import',
    einheit: item.einheit,
    preis: includePrices ? item.einzelpreis : 0,
    // "Preise mit importieren" abgewählt → Preis ist FEHLEND, nicht 0,00 €.
    // Zusätzlich: hat schon die QUELLE keinen Preis genannt (item.preisFehlt, z.B.
    // deterministischer Parser mit unitPriceCents null), bleibt das Flag auch bei
    // aktiviertem Preisimport gesetzt — die 0 wäre sonst eine erfundene Aussage.
    preisFehlt: includePrices ? (item.preisFehlt === true ? true : undefined) : true,
    umsatzsteuer: normalizedVat,
    menge: item.menge,
    isBedarfsposition: item.isBedarfsposition || undefined,
    isAlternativposition: item.isAlternativposition || undefined,
  }));
  const positions: Position[] = quoteItems.map(({ menge: _menge, ...item }) => ({
    ...item,
    originalId: item.originalId ?? null,
    positionNumber: item.positionNumber ?? null,
    gewerk: item.gewerk ?? null,
    name: item.name ?? null,
  }));

  return { quoteItems, positions };
}

export function mapPdfImportToOfferState(
  imported: Pick<PdfImportApiData, 'metadata' | 'positions'>,
  fallbacks: { fallbackOfferNumber: string; fallbackDate: string },
  options?: { includePrices?: boolean },
): PdfImportOfferStateDraft {
  const vatRate = normalizePdfImportVatRate(imported.metadata.vatRate);
  const rows = mapPdfImportPositionsToOfferRows(imported.positions, vatRate, options);

  return {
    ...rows,
    quoteHeader: {
      kunde: imported.metadata.customerName || '',
      anschrift: imported.metadata.customerAddress || '',
      angebot_nr: imported.metadata.documentNumber || fallbacks.fallbackOfferNumber,
      datum: imported.metadata.date || fallbacks.fallbackDate,
      bauvorhaben: imported.metadata.projectName || '',
      gewerk: '',
      auftrag: '',
      eingereichte_unterlagen: '',
      lz: '',
      vortext: imported.metadata.introText || '',
      schlusstext: imported.metadata.closingText || '',
      mwstSatz: vatRate,
    },
  };
}

export function applyPdfImportOfferRows(
  draft: PdfImportOfferDraft,
  setters: {
    setPositions: (positions: Position[]) => void;
    setQuoteItems: (quoteItems: QuoteItem[]) => void;
  },
): void {
  // Store invariant: setPositions intentionally clears quoteItems. Preserve the import by
  // applying the persisted rows first and the editable quote rows second.
  setters.setPositions(draft.positions);
  setters.setQuoteItems(draft.quoteItems);
}

export function applyPdfImportOfferState(
  draft: PdfImportOfferStateDraft,
  setters: {
    setQuoteHeader: (header: QuoteHeader) => void;
    setPositions: (positions: Position[]) => void;
    setQuoteItems: (quoteItems: QuoteItem[]) => void;
  },
): void {
  // setQuoteItems derives its VAT from the current header, so the header must be applied
  // before the row adapter (including the valid 0% case).
  setters.setQuoteHeader(draft.quoteHeader);
  applyPdfImportOfferRows(draft, setters);
}
